import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from 'app'
import Api, { paths } from 'shared/services/Api'
import { Country, Validation, Language, LoadingStatus } from 'shared/types'
import * as selectors from './selectors'

export const getCountries = createAsyncThunk<
  { response: Country[] },
  undefined,
  { rejectValue: string }
>('getCountries', async (_, thunkAPI) => {
  try {
    const response = await Api.get(paths.Countries.root, {
      withCredentials: false,
    })
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getValidation = createAsyncThunk<
  { response: Validation },
  undefined,
  { rejectValue: string }
>('getValidation', async (_, thunkAPI) => {
  try {
    const response = await Api.get(paths.Settings.validation)
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getLanguages = createAsyncThunk<
  Language[],
  { disableRefetch?: boolean } | undefined,
  { rejectValue: string }
>(
  'getLanguages',
  async (_, thunkAPI) => {
    try {
      const response = await Api.get(paths.Languages.root)
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = selectors.getLanguages(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const getSelectedLanguage = createAsyncThunk<
  Language | null,
  { disableRefetch?: boolean } | undefined,
  { rejectValue: string }
>(
  'getSelectedLanguage',
  async (_, thunkAPI) => {
    try {
      const response = await Api.get(paths.Accounts.language)
      return response.data || null
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = selectors.getSelectedLanguage(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const setLanguage = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>('setLanguage', async (languageId, thunkAPI) => {
  try {
    await Api.put(paths.Accounts.language, {
      languageId,
    })
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})
